// Объединенная система ролей для Event Master
// Основана на анализе документов проекта

// Импорт новых enum соответствующих бэкенду
import { InternalRole } from './internal-roles.enum';
import { EventRole } from './event-roles.enum';

// Экспорт новых enum для использования
export { InternalRole } from './internal-roles.enum';
export { EventRole } from './event-roles.enum';

// @deprecated Используйте InternalRole из internal-roles.enum.ts
export enum SystemRole {
  ADMIN = 'admin',                  // Администратор системы
  MANAGER = 'manager',              // Менеджер платформы
  USER = 'user',                    // Обычный пользователь
  GUEST = 'guest'                   // Гость без регистрации
}


// Роли в рамках сотрудничества
export enum CollaborationRole {
  OWNER = 'owner',                  // Владелец проекта
  EDITOR = 'editor',                // Редактор
  VIEWER = 'viewer',                // Наблюдатель
  COMMENTER = 'commenter'           // Комментатор
}

// Организаторские иерархические роли (из Step-by-step Guide.md)
export enum OrganizerRole {
  MAIN_ORGANIZER = 'main_organizer',     // Главный организатор
  ORG_EVENT_MANAGER = 'org_event_manager', // Менеджер мероприятий (организаторский)
  COORDINATOR = 'coordinator',           // Координатор
  ASSISTANT = 'assistant'                // Помощник
}

// Контекстные разрешения для ролей
export enum RolePermission {
  // Основные права
  READ = 'read',
  WRITE = 'write', 
  DELETE = 'delete',
  MANAGE = 'manage',
  
  // Специфичные права
  CREATE_EVENT = 'create_event',
  INVITE_PARTICIPANTS = 'invite_participants',
  MANAGE_BUDGET = 'manage_budget',
  APPROVE_SUPPLIERS = 'approve_suppliers',
  VIEW_ANALYTICS = 'view_analytics',
  MANAGE_PAYMENTS = 'manage_payments'
}

// Объединенный тип для всех ролей (для переключателя)
export type UserContextRole = EventRole | InternalRole | CollaborationRole | OrganizerRole | SystemRole;

// Группировка ролей по категориям для UI
export const RoleCategories = {
  PRIMARY: [
    EventRole.ORGANIZER,
    EventRole.PARTICIPANT, 
    EventRole.SPEAKER,
    EventRole.SUPPLIER
  ],
  MANAGEMENT: [
    EventRole.EVENT_ADMIN,
    OrganizerRole.MAIN_ORGANIZER,
    OrganizerRole.ORG_EVENT_MANAGER,
    OrganizerRole.COORDINATOR,
    InternalRole.PLATFORM_MANAGER
  ],
  SPECIALIZED: [
    EventRole.EXECUTOR,
    OrganizerRole.ASSISTANT
  ],
  SYSTEM: [
    InternalRole.SUPER_ADMIN,
    InternalRole.PLATFORM_ADMIN,
    InternalRole.PLATFORM_MANAGER,
    InternalRole.CONTENT_MODERATOR,
    InternalRole.SUPPORT_AGENT
  ]
} as const;

// Права по ролям (базовая матрица)
export const RolePermissions: Record<UserContextRole, RolePermission[]> = {
  // Backend Event Roles
  [EventRole.ORGANIZER]: [
    RolePermission.READ, RolePermission.WRITE, RolePermission.MANAGE,
    RolePermission.CREATE_EVENT, RolePermission.INVITE_PARTICIPANTS,
    RolePermission.MANAGE_BUDGET, RolePermission.APPROVE_SUPPLIERS,
    RolePermission.VIEW_ANALYTICS
  ],
  [EventRole.PARTICIPANT]: [
    RolePermission.READ
  ],
  [EventRole.SPEAKER]: [
    RolePermission.READ, RolePermission.WRITE
  ],
  [EventRole.SUPPLIER]: [
    RolePermission.READ, RolePermission.WRITE,
    RolePermission.MANAGE_PAYMENTS
  ],
  [EventRole.EVENT_ADMIN]: [
    RolePermission.READ, RolePermission.WRITE, RolePermission.MANAGE,
    RolePermission.INVITE_PARTICIPANTS, RolePermission.VIEW_ANALYTICS
  ],
  [EventRole.CO_ORGANIZER]: [
    RolePermission.READ, RolePermission.WRITE, RolePermission.MANAGE,
    RolePermission.INVITE_PARTICIPANTS, RolePermission.VIEW_ANALYTICS
  ],
  [EventRole.EXECUTOR]: [
    RolePermission.READ, RolePermission.WRITE
  ],
  
  // Internal Roles  
  [InternalRole.SUPER_ADMIN]: [
    RolePermission.READ, RolePermission.WRITE, 
    RolePermission.DELETE, RolePermission.MANAGE
  ],
  [InternalRole.PLATFORM_ADMIN]: [
    RolePermission.READ, RolePermission.WRITE, RolePermission.MANAGE,
    RolePermission.VIEW_ANALYTICS
  ],
  [InternalRole.PLATFORM_MANAGER]: [
    RolePermission.READ, RolePermission.WRITE, RolePermission.MANAGE,
    RolePermission.VIEW_ANALYTICS,
    RolePermission.CREATE_EVENT,
    RolePermission.INVITE_PARTICIPANTS,
    RolePermission.MANAGE_BUDGET,
    RolePermission.APPROVE_SUPPLIERS,
  ],
  [InternalRole.CONTENT_MODERATOR]: [
    RolePermission.READ, RolePermission.WRITE, RolePermission.MANAGE
  ],
  [InternalRole.SUPPORT_AGENT]: [
    RolePermission.READ, RolePermission.WRITE
  ],
  [InternalRole.SECURITY_ADMIN]: [
    RolePermission.READ, RolePermission.WRITE, RolePermission.MANAGE
  ],
  [InternalRole.TECHNICAL_SUPPORT]: [
    RolePermission.READ, RolePermission.WRITE
  ],
  [InternalRole.CUSTOMER_SUCCESS]: [
    RolePermission.READ, RolePermission.WRITE
  ],
  [InternalRole.PRODUCT_MANAGER]: [
    RolePermission.READ, RolePermission.WRITE, RolePermission.MANAGE,
    RolePermission.VIEW_ANALYTICS
  ],
  [InternalRole.DEVELOPER]: [
    RolePermission.READ, RolePermission.WRITE
  ],
  [InternalRole.QA_ENGINEER]: [
    RolePermission.READ, RolePermission.WRITE
  ],
  [InternalRole.OPERATIONS_MANAGER]: [
    RolePermission.READ, RolePermission.WRITE, RolePermission.MANAGE
  ],
  [InternalRole.MONITORING_ENGINEER]: [
    RolePermission.READ, RolePermission.WRITE
  ],
  [InternalRole.COMMUNITY_MANAGER]: [
    RolePermission.READ, RolePermission.WRITE
  ],
  
  // Legacy System Roles (для обратной совместимости)
  [SystemRole.ADMIN]: [
    RolePermission.READ, RolePermission.WRITE, 
    RolePermission.DELETE, RolePermission.MANAGE
  ],
  [SystemRole.MANAGER]: [
    RolePermission.READ, RolePermission.WRITE, RolePermission.MANAGE,
    RolePermission.VIEW_ANALYTICS
  ],
  [SystemRole.USER]: [
    RolePermission.READ, RolePermission.WRITE
  ],
  [SystemRole.GUEST]: [
    RolePermission.READ
  ],
  
  // Collaboration Roles
  [CollaborationRole.OWNER]: [
    RolePermission.READ, RolePermission.WRITE, 
    RolePermission.DELETE, RolePermission.MANAGE
  ],
  [CollaborationRole.EDITOR]: [
    RolePermission.READ, RolePermission.WRITE
  ],
  [CollaborationRole.VIEWER]: [
    RolePermission.READ
  ],
  [CollaborationRole.COMMENTER]: [
    RolePermission.READ
  ],
  
  // Organizer Roles
  [OrganizerRole.MAIN_ORGANIZER]: [
    RolePermission.READ, RolePermission.WRITE, RolePermission.MANAGE,
    RolePermission.CREATE_EVENT, RolePermission.INVITE_PARTICIPANTS,
    RolePermission.MANAGE_BUDGET, RolePermission.APPROVE_SUPPLIERS,
    RolePermission.VIEW_ANALYTICS, RolePermission.MANAGE_PAYMENTS
  ],
  [OrganizerRole.ORG_EVENT_MANAGER]: [
    RolePermission.READ, RolePermission.WRITE, RolePermission.MANAGE,
    RolePermission.INVITE_PARTICIPANTS, RolePermission.VIEW_ANALYTICS
  ],
  [OrganizerRole.COORDINATOR]: [
    RolePermission.READ, RolePermission.WRITE,
    RolePermission.INVITE_PARTICIPANTS
  ],
  [OrganizerRole.ASSISTANT]: [
    RolePermission.READ, RolePermission.WRITE
  ]
}; 